import { FC } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import { makeStyles } from 'tss-react/mui'
import { theme } from '../../lib/styles/universal'
import {
    formatInterestSubscriptionLabel,
    InterestedOrganizationSubscriptionType,
    PricingQuoteResult,
} from '@hazadapt-git/public-core-base'
import {
    formatInterestPricingAmount,
    getInterestCommunitySizeLabel,
} from '../../lib/utils/organization'

interface InterestPricingQuoteProps {
    quote: PricingQuoteResult
    subscription_type: InterestedOrganizationSubscriptionType
}

export const InterestPricingQuote: FC<InterestPricingQuoteProps> = ({
    quote,
    subscription_type,
}) => {
    const { classes: localClasses } = useLocalStyles()
    const subscriptionLabel = formatInterestSubscriptionLabel(subscription_type)
    const isYearly =
        subscription_type === InterestedOrganizationSubscriptionType.YEARLY
    const amount = isYearly ? quote.yearly : quote.monthly
    const display = formatInterestPricingAmount(amount)
    const communityLabel = getInterestCommunitySizeLabel(quote.community_size)

    return (
        <Paper variant="outlined" className={localClasses.card}>
            <Stack gap="0.25rem" className={localClasses.stackFill}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="0.5rem"
                    className={localClasses.stackFill}
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Estimated Pricing
                    </Typography>
                    <Chip
                        label={`ResiliencePoint ${subscriptionLabel}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        className={localClasses.subscriptionChip}
                    />
                </Stack>
                <Typography variant="h3" component="p" fontWeight={700}>
                    {display}
                </Typography>
                {communityLabel ? (
                    <Typography variant="body2" color="text.secondary">
                        {communityLabel}
                    </Typography>
                ) : null}
                {isYearly && (
                    <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight={600}
                    >
                        Includes two months free
                    </Typography>
                )}
            </Stack>
        </Paper>
    )
}

const useLocalStyles = makeStyles()({
    card: {
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        borderRadius: '0.75rem',
        padding: '1rem 1.25rem',
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}08`,
    },
    stackFill: {
        width: '100%',
        minWidth: 0,
    },
    subscriptionChip: {
        alignSelf: 'stretch',
        maxWidth: '100%',
        '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        [theme.breakpoints.up('sm')]: {
            alignSelf: 'auto',
        },
    },
})
